import { useCallback, useEffect, useRef } from "react";

/**
 * Broadcasts when opening and closes when another opener announces, so only one stays open.
 *
 * @param {string} eventName - The window event name to broadcast and listen on
 * @param {() => void} onClose - Called when another announces open
 * @param {string[]} additionalBroadcasts - Optional extra events to broadcast when opening
 * @returns {{ announceOpen: () => void, idRef: React.MutableRefObject<string> }}
 */
export function useCloseOthersOnOpen(eventName, onClose, additionalBroadcasts = []) {
  const idRef = useRef(Math.random().toString(36).slice(2));

  useEffect(() => {
    const handler = (e) => {
      if (!e || !e.detail) return;
      if (e.detail.id !== idRef.current) {
        onClose?.();
      }
    };
    window?.addEventListener(eventName, handler);
    return () => window?.removeEventListener(eventName, handler);
  }, [eventName, onClose]);

  const announceOpen = useCallback(() => {
    try {
      window?.dispatchEvent(new CustomEvent(eventName, { detail: { id: idRef.current } }));
      for (const extra of additionalBroadcasts) {
        window?.dispatchEvent(new CustomEvent(extra, { detail: { id: idRef.current } }));
      }
    } catch (_) {}
  }, [eventName, additionalBroadcasts]);

  return { announceOpen, idRef };
}

export default useCloseOthersOnOpen;
